// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: Connections → Microsoft: pick which Teams group chats mirror into
// Konvo groups. Linking creates the Konvo group (shadow accounts for people
// without Konvo); the switch pauses Konvo → Teams for a noisy one.

import {Routes} from '@app/app/Routes';
import styles from '@app/features/konvo_connections/components/KonvoConnectorCard.module.css';
import KonvoTeamsGroups, {type TeamsGroupChat} from '@app/features/konvo_connections/state/KonvoTeamsGroups';
import {transitionTo} from '@app/features/navigation/utils/RouterUtils';
import {Button} from '@app/features/ui/button/Button';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {Switch} from '@app/features/ui/components/form/FormSwitch';
import {TeamsLogo} from '@app/features/ui/components/icons/TeamsLogo';
import {msg} from '@lingui/core/macro';
import {Plural, Trans, useLingui} from '@lingui/react/macro';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import {useEffect, useState} from 'react';
import ui from './KonvoTeamsGroupsCard.module.css';

const TITLE_DESCRIPTOR = msg({message: 'Teams group chats', comment: 'Connections card title.'});
const DESCRIPTION_DESCRIPTOR = msg({
	message:
		'Mirror a Teams group chat into a Konvo group. Everyone in it gets the messages here, and what you type in the Konvo group goes back to Teams.',
	comment: 'Connections card description.',
});
const LINK_DESCRIPTOR = msg({message: 'Mirror', comment: 'Button: start mirroring a Teams group chat.'});
const UNLINK_DESCRIPTOR = msg({message: 'Stop mirroring', comment: 'Button: stop mirroring a Teams group chat.'});
const MIRROR_SWITCH_DESCRIPTOR = msg({message: 'Mirror to Teams', comment: 'Switch label on a mirrored Teams group.'});
const LINKED_BY_OTHER_DESCRIPTOR = msg({
	message: 'Mirrored by someone else',
	comment: 'Note under a Teams group chat another Konvo user already mirrors.',
});
const MEMBERS_DESCRIPTOR = msg({message: '{count} members', comment: 'Member count under a Teams group chat.'});
const EMPTY_DESCRIPTOR = msg({message: 'No Teams group chats found.', comment: 'Empty state.'});
const LOADING_DESCRIPTOR = msg({message: 'Loading your Teams group chats…', comment: 'Loading state.'});
const UNREACHABLE_DESCRIPTOR = msg({
	message: 'Teams did not answer. Check the Microsoft connection and try again.',
	comment: 'Error state.',
});
const RETRY_DESCRIPTOR = msg({message: 'Try again', comment: 'Button.'});
const LINKED_TOAST_DESCRIPTOR = msg({
	message: '{name} now mirrors into Konvo',
	comment: 'Toast after a Teams group chat is linked.',
});
const UNLINKED_TOAST_DESCRIPTOR = msg({
	message: '{name} no longer mirrors',
	comment: 'Toast after a Teams group chat is unlinked.',
});
const NOT_SUBSCRIBED_TOAST_DESCRIPTOR = msg({
	message: 'Mirroring is on, but Teams could not be subscribed for new messages. Reconnect Microsoft.',
	comment: 'Toast when linking worked but the Graph subscription failed.',
});
const LINK_FAILED_TOAST_DESCRIPTOR = msg({
	message: 'Could not mirror that chat.',
	comment: 'Toast when linking a Teams group chat fails.',
});

const GroupRow = observer(({chat}: {chat: TeamsGroupChat}) => {
	const {i18n} = useLingui();
	const busy = KonvoTeamsGroups.busyChatId === chat.chat_id;
	const foreign = chat.linked && !chat.owner;
	const onToggle = async () => {
		try {
			if (chat.linked) {
				await KonvoTeamsGroups.unlink(chat.chat_id);
				ToastCommands.success(i18n._(UNLINKED_TOAST_DESCRIPTOR, {name: chat.name}));
			} else {
				const result = await KonvoTeamsGroups.link(chat.chat_id);
				ToastCommands.success(i18n._(LINKED_TOAST_DESCRIPTOR, {name: result?.name ?? chat.name}));
				if (result && result.subscribed === false) ToastCommands.error(i18n._(NOT_SUBSCRIBED_TOAST_DESCRIPTOR));
			}
		} catch {
			ToastCommands.error(i18n._(LINK_FAILED_TOAST_DESCRIPTOR));
		}
	};
	return (
		<div className={ui.row} data-flx="konvo-connections.teams-groups-card.row">
			<div className={ui.rowInfo}>
				<div
					className={styles.name}
					style={{overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}
					title={chat.members.join(', ')}
				>
					{chat.name}
				</div>
				<div className={styles.account}>
					{foreign ? i18n._(LINKED_BY_OTHER_DESCRIPTOR) : i18n._(MEMBERS_DESCRIPTOR, {count: chat.member_count})}
				</div>
			</div>
			<div className={ui.rowActions}>
				{chat.linked && chat.channel_id && (
					<Button
						variant="secondary"
						small
						onClick={() => {
							ModalCommands.popAll();
							transitionTo(Routes.dmChannel(chat.channel_id!));
						}}
					>
						<Trans>Open group</Trans>
					</Button>
				)}
				{chat.linked && chat.owner && (
					<details className={ui.more}>
						<summary aria-label={i18n._(msg`Manage ${chat.name}`)}>•••</summary>
						<div className={ui.moreContent}>
							<Switch
								value={chat.mirror}
								onChange={(value) =>
									void KonvoTeamsGroups.setMirror(chat.chat_id, value).catch(() =>
										ToastCommands.error(i18n._(msg`Could not update mirroring.`)),
									)
								}
								ariaLabel={i18n._(MIRROR_SWITCH_DESCRIPTOR)}
								compact
							/>
							<span className={styles.note}>{i18n._(MIRROR_SWITCH_DESCRIPTOR)}</span>
							<Button
								variant="secondary"
								small
								submitting={busy}
								disabled={KonvoTeamsGroups.busyChatId !== null}
								onClick={() => void onToggle()}
							>
								{i18n._(UNLINK_DESCRIPTOR)}
							</Button>
						</div>
					</details>
				)}
				{!chat.linked && (
					<Button
						variant="secondary"
						small
						submitting={busy}
						disabled={KonvoTeamsGroups.busyChatId !== null}
						onClick={() => void onToggle()}
					>
						{i18n._(LINK_DESCRIPTOR)}
					</Button>
				)}
			</div>
		</div>
	);
});

export const KonvoTeamsGroupsCard = observer(({onManage}: {onManage: () => void}) => {
	const {i18n} = useLingui();
	useEffect(() => {
		if (KonvoTeamsGroups.chats === null) void KonvoTeamsGroups.loadChats();
	}, []);
	const count = KonvoTeamsGroups.chats?.filter((chat) => chat.linked).length ?? 0;
	return (
		<div className={styles.card} data-flx="konvo-connections.teams-groups-card">
			<div className={styles.top}>
				<div className={styles.logoTile}>
					<TeamsLogo size={24} />
				</div>
				<div className={styles.heading}>
					<div className={styles.name}>{i18n._(TITLE_DESCRIPTOR)}</div>
					<div className={styles.account} role="status">
						{KonvoTeamsGroups.chatsError ? (
							i18n._(UNREACHABLE_DESCRIPTOR)
						) : KonvoTeamsGroups.chats === null ? (
							i18n._(LOADING_DESCRIPTOR)
						) : (
							<Plural value={count} one="# chat mirrored" other="# chats mirrored" />
						)}
					</div>
				</div>
				<span className={clsx(styles.status, count > 0 && styles.statusConnected)}>
					<Trans>Available</Trans>
				</span>
			</div>
			<div className={styles.description}>
				<Trans>Sync messages between Teams and Konvo.</Trans>
			</div>
			<div className={styles.actions}>
				<Button variant="secondary" small onClick={onManage}>
					{count > 0 ? <Trans>Manage mirrored chats →</Trans> : <Trans>Choose chats →</Trans>}
				</Button>
			</div>
		</div>
	);
});

export const KonvoTeamsGroupsManager = observer(({onBack}: {onBack: () => void}) => {
	const {i18n} = useLingui();
	const [query, setQuery] = useState('');
	const [filter, setFilter] = useState<'mirrored' | 'available'>(() =>
		KonvoTeamsGroups.chats?.some((chat) => chat.linked) ? 'mirrored' : 'available',
	);
	useEffect(() => {
		void KonvoTeamsGroups.loadChats();
	}, []);
	const chats = KonvoTeamsGroups.chats ?? [];
	const mirrored = chats.filter((chat) => chat.linked).length;
	const available = chats.length - mirrored;
	const visible = chats.filter(
		(chat) =>
			chat.linked === (filter === 'mirrored') &&
			`${chat.name} ${chat.members.join(' ')}`.toLocaleLowerCase().includes(query.trim().toLocaleLowerCase()),
	);
	return (
		<section className={ui.manager} aria-label={i18n._(TITLE_DESCRIPTOR)}>
			<div className={ui.header}>
				<Button variant="ghost" small fitContent onClick={onBack}>
					<Trans>← Connections</Trans>
				</Button>
				<h2 className={styles.name}>{i18n._(TITLE_DESCRIPTOR)}</h2>
				<p className={styles.description}>{i18n._(DESCRIPTION_DESCRIPTOR)}</p>
				<input
					className={ui.search}
					type="search"
					value={query}
					onChange={(event) => setQuery(event.target.value)}
					placeholder={i18n._(msg`Search chats…`)}
					aria-label={i18n._(msg`Search chats`)}
				/>
				<div className={ui.filters} role="group" aria-label={i18n._(msg`Filter chats`)}>
					<button type="button" aria-pressed={filter === 'mirrored'} onClick={() => setFilter('mirrored')}>
						<Trans>Mirrored ({mirrored})</Trans>
					</button>
					<button type="button" aria-pressed={filter === 'available'} onClick={() => setFilter('available')}>
						<Trans>Available ({available})</Trans>
					</button>
				</div>
			</div>
			<div className={ui.list} aria-busy={KonvoTeamsGroups.loadingChats}>
				{KonvoTeamsGroups.chatsError ? (
					<div className={ui.empty} role="alert">
						<p>{i18n._(UNREACHABLE_DESCRIPTOR)}</p>
						<Button variant="secondary" small onClick={() => void KonvoTeamsGroups.loadChats()}>
							{i18n._(RETRY_DESCRIPTOR)}
						</Button>
					</div>
				) : KonvoTeamsGroups.chats === null ? (
					<p className={ui.empty} role="status">
						{i18n._(LOADING_DESCRIPTOR)}
					</p>
				) : visible.length === 0 ? (
					<p className={ui.empty} role="status">
						{query.trim() ? (
							<Trans>No chats match your search.</Trans>
						) : chats.length === 0 ? (
							i18n._(EMPTY_DESCRIPTOR)
						) : filter === 'mirrored' ? (
							<Trans>No mirrored chats yet. Choose a chat from Available to get started.</Trans>
						) : (
							<Trans>All your chats are mirrored.</Trans>
						)}
					</p>
				) : (
					visible.map((chat) => <GroupRow key={chat.chat_id} chat={chat} />)
				)}
			</div>
		</section>
	);
});
